import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { track } from './analytics';

// The app origin every conversion CTA points at. A delegated click listener
// catches clicks on ANY anchor to this host, so we don't have to thread an
// onClick through every CTA component (Navbar, Hero, CTA, BlogPost, ...).
const APP_HOST = 'app.omnicanvasnotes.com';

/**
 * Marketing-site funnel instrumentation: a `page_view` on every route change
 * and a `cta_click` whenever a visitor clicks a link into the app. Mounted once
 * inside <BrowserRouter> (alongside useSignupAttribution). Client-only — the
 * effects are no-ops during SSR.
 */
export function useMarketingAnalytics(): void {
  const location = useLocation();

  // page_view on initial load and every client-side navigation.
  useEffect(() => {
    track('page_view', { path: location.pathname + location.search });
  }, [location.pathname, location.search]);

  // Delegated CTA tracking for app links and the account-free live demo.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a') as HTMLAnchorElement | null;
      if (!anchor?.href) return;
      let url: URL;
      try {
        url = new URL(anchor.href);
      } catch {
        return;
      }
      const from = window.location.pathname;
      const label = (anchor.textContent || '').trim().slice(0, 60) || undefined;
      if (url.host === APP_HOST) {
        // utm_content encodes which CTA was clicked (`top_blog_cta`,
        // `bottom_blog_cta`, ...). Without it every CTA on the page collapsed
        // into one undifferentiated `cta_click`, so per-position click-through
        // could not be compared against the per-position impression events.
        const content = url.searchParams.get('utm_content') || undefined;
        track('cta_click', {
          href: url.href,
          mode: url.searchParams.get('mode') || undefined,
          template: url.searchParams.get('template') || undefined,
          action: url.searchParams.get('action') || undefined,
          campaign: url.searchParams.get('utm_campaign') || undefined,
          content,
          position: content ? content.split('_')[0] : undefined,
          // Which CTA copy was on the page, so a rewrite can be compared
          // against the clicks it replaced (see BLOG_CTA_COPY_VERSION).
          copy: url.searchParams.get('cta_copy') || undefined,
          from,
          label,
        });
        return;
      }
      if (url.host === window.location.host && url.pathname === '/demo' && from !== '/demo') {
        track('demo_click', {
          from,
          label,
          campaign: url.searchParams.get('utm_campaign') || undefined,
          content: url.searchParams.get('utm_content') || undefined,
        });
      }
    };
    // Capture phase so we still record the event even if the handler navigates.
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}
